/**
 * Portfolio · « Le Grand Quiz du Lair » : la page est privée (réservée aux lives), alors on la montre ici
 * avec ses vrais composants : le logo animé, les 15 épreuves et leurs présentateurs, et deux mini-démos jouables.
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FORMATS, HOSTS } from './quizShowFormats';
import { Emblem, ShowLogo } from './QuizShowLogos';
import { AnyAvatar, UNIVERSES } from './QuizShowAvatars';

const IMG = `${process.env.PUBLIC_URL || ''}/portfolio`;

const UNDER_THE_HOOD = [
  ['Éditeur piloté par schémas', 'Chaque épreuve décrit ses champs (textes, images, listes, cases à cocher) et ses règles de validation : un seul éditeur générique sert les 15 formats et signale ce qui manque avant le live.'],
  ['Médias hébergés', 'Images, extraits audio et voix enregistrées envoyés depuis l’éditeur, stockés dans MongoDB (GridFS) et servis par l’API ; un lien vers une image en ligne fonctionne aussi.'],
  ['Présentateurs doublés au micro', 'Un présentateur par épreuve, avec sa réplique et une voix off enregistrée directement sur le site (MediaRecorder) grâce à un prompteur.'],
  ['Karaoké synchronisé', 'Lecteur YouTube piloté, paroles importées au format LRC ou calées au tap, coupure automatique sur chaque trou, une case par lettre et des jokers.'],
  ['Régie sur un second écran', 'L’animateur garde l’antisèche et les commandes sur une fenêtre à part, synchronisée en direct (BroadcastChannel), pendant que le stream ne montre que le plateau.'],
  ['Partie et classement côté serveur', 'Parties reprises après un rafraîchissement, bonus sur les épreuves délaissées, classement général et grille réinitialisable, le tout dans une API Express.']
];

/** Mini-démo · Pixel Mystère : l'image est redessinée en gros pixels sur un canvas. */
const LEVELS = [6, 10, 16, 26, 44];
function PixelDemo() {
  const cv = useRef(null);
  const [img, setImg] = useState(null);
  const [lv, setLv] = useState(0);
  useEffect(() => { const im = new Image(); im.onload = () => setImg(im); im.src = `${IMG}/arena.webp`; }, []);
  useEffect(() => {
    const c = cv.current; if (!c || !img || lv >= LEVELS.length) return;
    const W = c.width; const H = c.height;
    const r = Math.max(W / img.naturalWidth, H / img.naturalHeight);
    const w = img.naturalWidth * r; const h = img.naturalHeight * r;
    const sw = LEVELS[lv]; const sh = Math.max(1, Math.round(sw * (H / W)));
    const t = document.createElement('canvas'); t.width = sw; t.height = sh;
    t.getContext('2d').drawImage(img, (W - w) / 2 / r, (H - h) / 2 / r, W / r, H / r, 0, 0, sw, sh);
    const x = c.getContext('2d'); x.imageSmoothingEnabled = false; x.clearRect(0, 0, W, H); x.drawImage(t, 0, 0, sw, sh, 0, 0, W, H);
  }, [img, lv]);
  const done = lv >= LEVELS.length;
  return (
    <div className="pf-gq-demo">
      <div className="pf-gq-demo-head"><b>Pixel Mystère</b><span>{done ? 'Image révélée' : `Palier ${lv + 1} / 5 · vaut ${5 - lv} pt${5 - lv > 1 ? 's' : ''}`}</span></div>
      <div className="pf-gq-screen is-pixel">
        {done ? <img src={`${IMG}/arena.webp`} alt="Capture de Poké Bully Tactics" /> : <canvas ref={cv} width={640} height={400} />}
      </div>
      <div className="pf-gq-demo-bar">
        <button type="button" className="pf-btn is-main" disabled={done} onClick={() => setLv(lv + 1)}>Préciser l’image</button>
        <button type="button" className="pf-btn" onClick={() => setLv(0)}>Recommencer</button>
      </div>
    </div>
  );
}

/** Mini-démo · Les Fouilles Interdites : un mur 8 × 5 repéré A1 … H5, six coups de pioche. */
const COLS = 'ABCDEFGH';
function DigDemo() {
  const [holes, setHoles] = useState([]);
  const [shown, setShown] = useState(false);
  const left = 6 - holes.length;
  const dig = (k) => { if (shown || holes.includes(k) || left <= 0) return; setHoles((h) => [...h, k]); };
  return (
    <div className="pf-gq-demo">
      <div className="pf-gq-demo-head"><b>Les Fouilles Interdites</b><span>{shown ? 'Image révélée' : `${left} coup${left > 1 ? 's' : ''} de pioche · vaut ${holes.length ? Math.max(1, 7 - holes.length) : 6} pts`}</span></div>
      <div className="pf-gq-dig">
        <span />
        <div className="pf-gq-cols">{COLS.split('').map((c) => <b key={c}>{c}</b>)}</div>
        <div className="pf-gq-rows">{[1, 2, 3, 4, 5].map((r) => <b key={r}>{r}</b>)}</div>
        <div className="pf-gq-screen is-dig">
          <img src={`${IMG}/swarm.webp`} alt="Capture de Poké Swarm" />
          <div className="pf-gq-rocks">
            {Array.from({ length: 40 }, (_, k) => (
              <button type="button" key={k} aria-label={`Case ${COLS[k % 8]}${Math.floor(k / 8) + 1}`} className={holes.includes(k) || shown ? 'is-broken' : ''} style={{ '--r': ((k * 37) % 7) / 7 }} onClick={() => dig(k)}>{COLS[k % 8]}{Math.floor(k / 8) + 1}</button>
            ))}
          </div>
        </div>
      </div>
      <div className="pf-gq-demo-bar">
        <button type="button" className="pf-btn is-main" onClick={() => setShown(true)} disabled={shown}>Révéler</button>
        <button type="button" className="pf-btn" onClick={() => { setHoles([]); setShown(false); }}>Recommencer</button>
      </div>
    </div>
  );
}

export default function PortfolioGrandQuiz() {
  const [sel, setSel] = useState(FORMATS[0].id);
  const f = FORMATS.find((x) => x.id === sel) || FORMATS[0];
  const host = HOSTS[f.id] || {};
  const avatars = useMemo(() => UNIVERSES.reduce((s, u) => s + u.list.length, 0), []);
  const stats = [[FORMATS.length, 'épreuves'], [Object.keys(HOSTS).length, 'présentateurs'], [avatars, 'avatars, 4 univers'], ['3', 'jokers'], ['60+', 'contenus prêts']];
  return (
    <section id="pf-grandquiz" className="pf-gq">
      <div className="pf-gq-hero pf-reveal">
        <div className="pf-gq-logo"><ShowLogo size={460} /></div>
        <div>
          <span className="pf-kicker">Projet à la une · page privée</span>
          <h2 className="pf-h2">Le Grand Quiz du Lair</h2>
          <p className="pf-gq-lead">Un jeu télévisé complet pensé pour les lives : le candidat choisit 5 épreuves parmi 15, joue ses jokers, et chaque épreuve a son présentateur, son logo animé et ses propres règles. L’animateur pilote tout depuis une régie, prépare les contenus dans un éditeur intégré et suit un classement général.</p>
          <div className="pf-gq-stats">{stats.map(([v, l]) => <div key={l}><b>{v}</b><span>{l}</span></div>)}</div>
          <p className="pf-gq-note">La page est cachée et protégée par une clé : elle n’est utilisée qu’en live. Tout ce qui suit vient de son vrai code.</p>
        </div>
      </div>

      <div className="pf-gq-explorer pf-reveal">
        <h3>Les 15 épreuves</h3>
        <div className="pf-gq-grid">
          {FORMATS.map((x) => (
            <button type="button" key={x.id} className={`pf-gq-cell${x.id === sel ? ' is-on' : ''}`} style={{ '--c1': x.c1, '--c2': x.c2 }} onClick={() => setSel(x.id)} aria-pressed={x.id === sel} aria-label={x.name.join(' ')}>
              <Emblem f={x} still={x.id !== sel} />
            </button>
          ))}
        </div>
        <div className="pf-gq-detail" style={{ '--c1': f.c1, '--c2': f.c2 }} key={f.id}>
          <div className="pf-gq-host">
            <AnyAvatar v={host.host} name={host.name} size={96} />
            <div><b>{host.name}</b><span>présente</span></div>
          </div>
          <div className="pf-gq-detail-text">
            <h4>{f.name.join(' ')}</h4>
            <p className="pf-gq-line">« {host.line} »</p>
            <p>{f.pitch}</p>
            <ul>{(f.rules || []).map((r) => <li key={r}>{r}</li>)}</ul>
          </div>
        </div>
      </div>

      <div className="pf-gq-demos pf-reveal">
        <h3>À essayer ici</h3>
        <div className="pf-gq-demo-grid"><PixelDemo /><DigDemo /></div>
      </div>

      <div className="pf-gq-hood pf-reveal">
        <h3>Sous le capot</h3>
        <div className="pf-gq-hood-grid">
          {UNDER_THE_HOOD.map(([t, d]) => <div key={t}><b>{t}</b><p>{d}</p></div>)}
        </div>
        <div className="pf-tech">{['React', 'Node.js', 'Express', 'MongoDB / GridFS', 'Canvas', 'SVG animé', 'MediaRecorder', 'YouTube IFrame API', 'BroadcastChannel', 'Playwright'].map((t) => <span key={t}>{t}</span>)}</div>
      </div>
    </section>
  );
}
